import React, { useEffect, useState, useRef } from 'react';
import { Distributor } from '../types.ts';
import Slider from 'react-slick';
import MyDistributorsCarouselItem, { MyDistributorsCarouselItemProps } from './MyDistributorsCarouselItem';
import { getDistributors, deleteProjectDistributors } from '../api/api';
import { useAuthentication } from '../context/AuthenticationContext';
import ConfirmationPrompt from '../components/ConfirmationPrompt';
import { trackFindDistributorClick } from '../../analytics/my-dashboard';
import { trackDistributorInteraction } from '../analytics';
import { text } from '../data';
import toast from 'react-hot-toast';

const MyDistributorsCarousel: React.FC = () => {
	const distributorUrl = window?._timken?.distributorUrl as string ?? 'https://locations.timken.com/distributor-locator/';

    const { isLoggedIn, idToken } = useAuthentication();
    const [loading, setLoading] = useState(true);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [targetDistributorId, setTargetDistributorId] = useState('');
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [removalMessage, setRemovalMessage] = useState('');
    const targetDeleteLink = useRef<HTMLElement | null>(null);

    const sliderSettings = {
        dots: true,
        slidesToShow: 3,
        infinite: false,
        swipeToSlide: true,
        focusOnSelect: true,
        className: 't-my-dashboard__slider t-my-dashboard__slider--distributors',
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    const deleteDistributors = async (distributorId: string) => {
        if (!distributorId) {
            return;
        }

        setLoading(true);
        try {
            const toastId = toast.loading(text.loading);
            setIsConfirmOpen(false);
            await deleteProjectDistributors(distributorId);
            toast.success(`${removalMessage}`, {
                id: toastId,
                duration: 5000
            });
            const targetDistributor = findTargetDistributor(distributorId);
            triggerDeleteAnalytics(targetDistributor);

            await fetchDistributors();
        } catch (error) {
            toast.dismiss();
            toast.error(error.message);
        }
        setLoading(false);
    }

    const findTargetDistributor = (distributorId: string) => {
        return distributors.find(distributorItem => distributorItem.distributorId === distributorId);
    }

    const triggerDeleteConfirmation = (event, distributorId: string) => {
        event?.preventDefault();
        setTargetDistributorId(distributorId)
        const targetDistributor = findTargetDistributor(distributorId);
        targetDeleteLink.current = event.target;

        setConfirmationMessage(`${text.areYouSureWantToDelete} "${targetDistributor?.title}"?`);
        setRemovalMessage(`${targetDistributor?.title} ${text.hasBeenDeleted}`);
        setIsConfirmOpen(true);
    }

    const triggerDeleteAnalytics = (targetDistributor: MyDistributorsCarouselItemProps | undefined) => {
        const target_el_text = targetDeleteLink?.current?.innerHTML;
        if(!targetDistributor) {
            return;
        }
        const {title, address, category} = targetDistributor || {}
        trackDistributorInteraction(address, title, 'remove_saved_distributor_click', target_el_text, '', category);
    }

    const confirmAction = () => {
        deleteDistributors(targetDistributorId);
    }

    const fetchDistributors = async () => {
        getDistributors().then((response) => {
            const { distributors } = response || {};
            if(!response?.distributors) {
                console.warn('No distributors found');
                return;
            }
            const dashboardDistributors = distributors.map(buildDistributors);
            setDistributors(dashboardDistributors);
        }).catch((error) => {
            console.error('Error fetching distributors:', error);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (isLoggedIn && !!idToken) {
            fetchDistributors();
        }
    }, [isLoggedIn, idToken]);

    const [distributors, setDistributors] = useState<MyDistributorsCarouselItemProps[]>([]);

    const capitalizeWords = (str) => {
        const words = str.toLowerCase().split(' ');

        const capitalizedWords = words.map((word) => {
            if (word.length === 0) {
                return ''; // Handle empty strings if present
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        });

        return capitalizedWords.join(' ');
    };

    const buildFormattedAddress = (address: string) => {
        if (!address) {
            return '';
        }
        const addressParts = address.split(',');
        const streetAddress = addressParts[0];
        const city = addressParts[1];
        const addressPartsRemoved = new Set([streetAddress, city])
        const trimmedAddress = addressParts.filter(item => !addressPartsRemoved.has(item));
        return `${capitalizeWords(streetAddress)}, ${capitalizeWords(city)}, ${trimmedAddress.join(',')}`;
    }

    const parseContactInfo = (contactInfo: string) => {
        if (!contactInfo) {
            return {
                email: '',
                phone: ''
            }
        }
        const $tempContainer = document.createElement('div');
        $tempContainer.innerHTML = contactInfo;
        const $distributorLinks = $tempContainer.querySelectorAll('a');
        const contactUrls = [...$distributorLinks].map(($linkItem) => $linkItem.getAttribute('href'))
        const phoneLink = contactUrls.find(url => url?.indexOf('tel') > -1) ?? '';
        const emailLink = contactUrls.find(url => url?.indexOf('mailto') > -1) ?? '';
        const phone = phoneLink ? phoneLink?.split(':')[1] : '';
        const email = emailLink ? emailLink?.split(':')[1] : '';
        return {
            email,
            phone,
        }
    }

    const buildDistributors = (distributor: Distributor): MyDistributorsCarouselItemProps => {
        if (!distributor) {
            return {} as MyDistributorsCarouselItemProps;
        }
        const normalizedName = distributor.distributorName || '';

        const { marker_id, address, lat, lng, description, category } = distributor?.details || {};

        const coordinates = lat && lng ? `${lat},${lng}` : '';

        const { phone, email } = description ? parseContactInfo(description) : {};

        return {
            title: normalizedName || '',
            category,
            coordinates,
            markerId: marker_id,
            address: buildFormattedAddress(address),
            phone,
            email,
            websiteUrl: distributor.distributorURL,
            distributorCustomerNumber: distributor.distributorCustomerNumber,
            distributorId: distributor.distributorId,
            isActive: distributor.isActive,
        };
    };

	const triggerTrackFindDistributorClickAnalytics = (event, text: string, link: string) => {
		event.preventDefault();
		trackFindDistributorClick(text, link);

		const target = link ? '_blank' : '_self';
		const newWindow = window.open(link, target, 'noopener,noreferrer');
		if (newWindow) newWindow.opener = null;
	}

    return (
        <div className={`t-my-distributors-carousel ${loading ? 't-full-block-skeleton' : ''}`}>
            {distributors.length > 0 ? (
                <Slider {...sliderSettings}>
                    {distributors.map((distributor, index) => (
                        <MyDistributorsCarouselItem
                            key={distributor.distributorId || index}
                            coordinates={distributor.coordinates || ''}
                            distributorId={distributor.distributorId}
                            title={distributor.title || ''}
                            category={distributor.category}
                            address={distributor.address}
                            markerId={distributor.markerId}
                            phone={distributor.phone}
                            websiteUrl={distributor.websiteUrl}
                            isActive={distributor.isActive}
                            deleteAction={(event) => { triggerDeleteConfirmation(event, distributor?.distributorId || '') }}
                        />
                    ))}
                </Slider>
            ) : (
				<div className="t-my-distributors-carousel__empty">
					<p className='fw-bold t-my-distributors-carousel__empty--heading'>You currently have no distributors selected.</p>
					<a href="#" onClick={(e) =>
						triggerTrackFindDistributorClickAnalytics(e, text.clickForDistributors, distributorUrl)
					} className='btn--tertiary' rel="noopener noreferrer">{text.clickForDistributors}</a>
				</div>
			)}
			<ConfirmationPrompt
				isOpen={isConfirmOpen}
				onConfirm={confirmAction}
				onCancel={() => setIsConfirmOpen(false)}
                message={confirmationMessage} />

        </div>
    );
};

export default MyDistributorsCarousel;
